<template>
  <div class="tenantAdmin">
    <el-row class="header-row">
      <el-col :span="24">
        <h3 class="el-page-header">Create New Tenant</h3>
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ path: '/' }">Home</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: '/tenants' }">Tenants</el-breadcrumb-item>
          <el-breadcrumb-item>Create Tenant</el-breadcrumb-item>
        </el-breadcrumb>
      </el-col>
    </el-row>

    <el-card v-loading="loadingTenants" element-loading-spinner="atom-audit-loader" style="min-height: 400px;">
      <div v-if="!loadingTenants">
        <el-alert v-if="connectionError" type="error" show-icon :closable="false" :title="connectionError" style="margin-bottom: 12px;"> </el-alert>

        <el-form size="mini" label-position="top" @submit.native.prevent style="width:50%">
          <el-form-item label="New Tenant Name">
            <el-input v-model="tenantName" placeholder="Tenant name" maxlength="64" show-word-limit :disabled="anyStepRunning || anyStepComplete"> </el-input>
          </el-form-item>

          <el-form-item label="Source Template Database">
            <el-select v-if="!loadingTenants" v-model="templateDb" placeholder="Select or type template database name" :filterable="true" :clearable="true" :allow-create="true" :disabled="anyStepRunning || anyStepComplete" style="width: 100%;">
              <el-option v-for="option in templateDbOptions" :key="option.key" :label="option.value" :value="option.key"></el-option>
            </el-select>
          </el-form-item>

          <el-button class="hiaButton" plain round type="primary" size="mini" :disabled="!tenantName || !templateDb || loading || anyStepRunning" @click="createTenant" style="margin-top: 10px;">
            Create Tenant (All Steps)
          </el-button>
        </el-form>

        <div v-if="anyStepRunning || anyStepComplete || provisioning" class="provisioning-status" style="margin-top: 16px;">
          <h4 v-if="tenantName">Provisioning Tenant: {{ tenantName }}</h4>

          <el-progress v-if="provisioning" :percentage="Math.round(progress)" :status="error ? 'exception' : success ? 'success' : undefined"> </el-progress>

          <div v-if="provisioning" style="margin-top: 8px;"><strong>Status:</strong> {{ currentStage || 'Initializing…' }}</div>
        </div>

        <div style="margin-top: 16px;">
          <h4>Provisioning Steps</h4>
          <div style="margin-top: 12px;">
            <div v-for="step in statusSteps" :key="step.key" style="display: flex; align-items: center; margin: 8px 0; min-height: 32px; line-height: 32px;">
              <i :class="step.done ? 'el-icon-circle-check' : step.running ? 'el-icon-loading' : 'el-icon-time'" :style="{ color: step.done ? '#67C23A' : step.running ? '#409EFF' : '#909399', marginRight: '8px', width: '20px' }"></i>
              <span :style="{ fontWeight: currentStage === step.stage ? 'bold' : 'normal', marginRight: '12px', minWidth: '200px' }">{{ step.label }}</span>
              <div style="width: 100px; display: flex; align-items: center; justify-content: flex-start;">
                <el-button v-if="step.key !== 'complete'" class="hiaButton" plain round size="mini" type="primary" :disabled="!tenantName || step.done || step.running || (anyStepRunning && !step.done) || ((step.key === 'sql' || step.key === 'retention') && !templateDb)" @click="runStep(step)">
                  {{ step.running ? 'Running...' : step.done ? 'Done' : 'Run' }}
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <div style="margin-top: 12px;">
          <el-alert v-if="error" type="error" show-icon :closable="true" @close="error = null" :title="error" style="margin-bottom: 8px;"> </el-alert>

          <el-alert v-if="success" type="success" show-icon :closable="false" :title="`Success! Tenant created with ID: ${newTenantId}`"> </el-alert>

          <div v-if="success || (allStepsComplete && !provisioning)" style="margin-top: 12px;">
            <el-button class="hiaButton" plain round size="mini" @click="reset">Reset</el-button>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
  import * as signalR from '@microsoft/signalr'
  import axios from '@/utilities/axios-global'
  import { mapState } from 'vuex'

  export default {
    name: 'tenantAdmin',
    data() {
      return {
        tenantName: '',
        templateDb: '',
        provisioning: false,
        progress: 0,
        currentStage: '',
        error: null,
        success: false,
        newTenantId: null,
        instanceId: null,
        statusSteps: [
          { key: 'sql', label: 'Creating SQL Database', stage: 'Creating SQL Database', done: false, running: false },
          { key: 'retention', label: 'Inheriting Retention Policy', stage: 'Inheriting Retention Policy', done: false, running: false },
          { key: 'syncGroup', label: 'Adding to Sync Group', stage: 'Adding to Sync Group', done: false, running: false },
          { key: 'insertTenant', label: 'Insert Tenant Record', stage: 'Insert Tenant Record', done: false, running: false },
          { key: 'storage', label: 'Creating Storage Container', stage: 'Creating Storage Container', done: false, running: false },
          { key: 'setActive', label: 'Set Tenant Active', stage: 'Set Tenant Active', done: false, running: false },
          { key: 'complete', label: 'Complete', stage: 'Complete', done: false, running: false }
        ],
        stepResults: {
          insertTenant: null,
          sql: null,
          storage: null,
          setActive: null
        },

        loading: false,
        loadingTenants: false,
        tenants: [],
        connection: null,
        signalrReady: false,
        connectionError: null
      }
    },
    computed: {
      ...mapState('user', ['user', 'baseApiUrl']),
      anyStepRunning() {
        return this.statusSteps.some(s => s.running)
      },
      anyStepComplete() {
        return this.statusSteps.some(s => s.done)
      },
      allStepsComplete() {
        return this.statusSteps.filter(s => s.key !== 'complete').every(s => s.done)
      },
      templateDbOptions() {
        return this.tenants.map(tenant => ({
          key: tenant.dbName,
          value: tenant.dbName
        }))
      }
    },
    async mounted() {
      // SignalR will be initialized lazily when provisioning starts
    },
    beforeDestroy() {
      if (this.connection) {
        this.connection.stop()
      }
    },
    methods: {
      getCurrentUserId() {
        // Use Auth0Id as the global unique identifier
        return this.user?.auth0Id || null
      },
      async initializeSignalR() {
        // If already connected, return
        if (this.signalrReady && this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
          return
        }

        // If connection exists but disconnected, try to restart it
        if (this.connection && this.connection.state === signalR.HubConnectionState.Disconnected) {
          try {
            await this.connection.start()
            this.signalrReady = true
            this.connectionError = null
            return
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('SignalR restart error', err)
            this.connectionError = 'Failed to reconnect SignalR connection.'
            this.signalrReady = false
            // Fall through to create a new connection
          }
        }

        // If already connecting, wait a bit and check again
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connecting) {
          // Wait for connection to complete (with timeout)
          const maxWait = 5000 // 5 seconds
          const startTime = Date.now()
          while (this.connection.state === signalR.HubConnectionState.Connecting && Date.now() - startTime < maxWait) {
            await new Promise(resolve => setTimeout(resolve, 100))
          }
          if (this.connection.state === signalR.HubConnectionState.Connected) {
            this.signalrReady = true
            return
          }
        }

        this.connectionError = null
        this.signalrReady = false

        const userId = this.getCurrentUserId()
        if (!userId) {
          this.connectionError = 'Missing current user id; cannot establish SignalR connection.'
          return
        }

        try {
          // Negotiate connection info through backend controller
          const negotiateResponse = await axios.post(`${this.baseApiUrl}/tenantAdmin/negotiate?userId=${encodeURIComponent(userId)}`)

          const { url, accessToken } = negotiateResponse.data || {}
          if (!url || !accessToken) {
            throw new Error('Negotiate response missing url/accessToken')
          }

          this.connection = new signalR.HubConnectionBuilder()
            .withUrl(url, { accessTokenFactory: () => accessToken })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning) // Reduced from Information
            .build()

          this.connection.on('tenantProvisioningProgress', update => {
            // Update can arrive as object or array (depending on SignalR binding)
            const u = Array.isArray(update) ? update[0] : update
            if (!u) return

            // SignalR payloads from Functions commonly serialize as PascalCase
            const stage = u.stage || u.Stage || ''
            const progress = typeof u.progress === 'number' ? u.progress : typeof u.Progress === 'number' ? u.Progress : 0
            // Handle success as boolean, string, or null
            let success = null
            if (typeof u.success === 'boolean') {
              success = u.success
            } else if (typeof u.Success === 'boolean') {
              success = u.Success
            } else if (u.success === 'true' || u.Success === 'true') {
              success = true
            } else if (u.success === 'false' || u.Success === 'false') {
              success = false
            }
            const tenantId = u.tenantId || u.TenantId || null
            const errorMessage = u.errorMessage || u.ErrorMessage || u.error || null

            this.currentStage = stage
            this.progress = progress
            this.updateStatusSteps(stage)

            // Handle individual step updates first
            const step = this.statusSteps.find(s => s.stage === stage)

            if (step) {
              // Check if this is a failure message
              // A failure is when errorMessage is present
              // Ignore initial "starting" messages where success=false, progress=0, and no errorMessage
              // These are just progress updates, not failures
              const isFailure = !!errorMessage

              if (isFailure) {
                // Step failed
                step.running = false
                step.done = false
                this.error = errorMessage || `Step "${step.label}" failed`
                this.provisioning = false
                // eslint-disable-next-line no-console
                console.error('Step failed:', { stage, errorMessage, success, stepKey: step.key, stepLabel: step.label })
              } else {
                // Check if this is a completion message (progress = 100 or success = true)
                // Use strict equality for success to ensure it's actually true (not just truthy)
                const isComplete = progress === 100 || success === true
                if (isComplete) {
                  step.running = false
                  step.done = true
                  this.provisioning = false // Stop provisioning when individual step completes
                  // Clear any previous errors for this step
                  if (this.error && this.error.includes(step.label)) {
                    this.error = null
                  }
                  // Store result for steps that return connection strings
                  if (step.key === 'sql' || step.key === 'storage') {
                    // Store the result if it's a connection string
                    const resultStr = tenantId ? String(tenantId) : null
                    if (resultStr) {
                      this.stepResults[step.key] = resultStr
                    }
                  }
                  // Update progress for UI
                  const stepIdx = this.statusSteps.findIndex(s => s.key === step.key)
                  if (stepIdx >= 0) {
                    this.progress = Math.round(((stepIdx + 1) / this.statusSteps.filter(s => s.key !== 'complete').length) * 100)
                  }
                  // Store tenant ID if returned
                  if (tenantId) {
                    this.newTenantId = tenantId
                  }
                } else {
                  // Step is in progress
                  step.running = true
                  step.done = false
                }
              }
            }

            // Handle overall completion/failure - only when stage is explicitly "Complete"
            // Don't mark all steps as done for individual step completions
            if (stage === 'Complete') {
              this.success = true
              this.newTenantId = tenantId
              this.provisioning = false
              // Mark all steps as done when complete
              this.statusSteps.forEach(s => {
                s.done = true
                s.running = false
              })
            } else if (stage === 'Failed' || (errorMessage && !step)) {
              // Global failure (not step-specific)
              this.error = errorMessage || 'Provisioning failed'
              this.provisioning = false
              // Mark running step as failed
              const runningStep = this.statusSteps.find(s => s.running)
              if (runningStep) {
                runningStep.running = false
              }
            }
          })

          await this.connection.start()
          this.signalrReady = true
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('SignalR init error', err)
          this.connectionError = 'Failed to establish real-time tenant admin connection.'
          this.signalrReady = false
        }
      },
      async createTenant() {
        if (!this.tenantName) return

        const userId = this.getCurrentUserId()
        if (!userId) {
          this.error = 'Missing current user id.'
          return
        }

        // Show spinner immediately when button is clicked
        this.loading = true
        this.provisioning = true
        this.progress = 0
        this.currentStage = 'Initializing…'
        this.error = null
        this.success = false
        this.newTenantId = null
        this.instanceId = null

        // Initialize SignalR connection before starting provisioning
        await this.initializeSignalR()
        if (!this.signalrReady) {
          // Check if the failure was due to missing userId
          if (this.connectionError && this.connectionError.includes('user id')) {
            this.error = this.connectionError
          } else {
            this.error = 'Failed to establish SignalR connection. Please try again.'
          }
          this.loading = false
          this.provisioning = false
          return
        }

        try {
          const response = await axios.post(`${this.baseApiUrl}/tenantAdmin/createTenant`, {
            tenantName: this.tenantName,
            adminUserId: String(userId),
            templateDb: this.templateDb || null
          })

          // Durable status payload varies; best-effort capture instance id if present
          this.instanceId = response?.data?.Id || response?.data?.id || response?.data?.instanceId || null
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('CreateTenant error', err)
          this.error = err?.response?.data?.message || err?.message || 'Failed to start tenant creation'
          this.provisioning = false
        } finally {
          this.loading = false
        }
      },
      reset() {
        this.tenantName = ''
        this.templateDb = ''
        this.provisioning = false
        this.progress = 0
        this.currentStage = ''
        this.error = null
        this.success = false
        this.newTenantId = null
        this.instanceId = null
        this.loading = false
        this.resetStatusSteps()
      },
      resetStatusSteps() {
        this.statusSteps.forEach(s => {
          s.done = false
          s.running = false
        })
        this.stepResults = {
          insertTenant: null,
          sql: null,
          storage: null,
          setActive: null
        }
      },
      updateStatusSteps(stage) {
        if (!stage) return
        if (stage === 'Complete') {
          this.statusSteps.forEach(s => {
            s.done = true
            s.running = false
          })
          return
        }

        const idx = this.statusSteps.findIndex(s => s.stage === stage)
        if (idx < 0) return

        // Mark steps before the current stage as completed (checkbox turns green once the step is done)
        this.statusSteps.forEach((s, i) => {
          s.done = i < idx
          s.running = i === idx
        })
      },
      async runStep(step) {
        if (!this.tenantName || step.done || step.running) return

        const userId = this.getCurrentUserId()
        if (!userId) {
          this.error = 'Missing current user id.'
          return
        }

        // Show spinner immediately when button is clicked
        step.running = true
        this.error = null
        this.provisioning = true

        // Initialize SignalR connection before starting step
        await this.initializeSignalR()
        if (!this.signalrReady) {
          // Check if the failure was due to missing userId
          if (this.connectionError && this.connectionError.includes('user id')) {
            this.error = this.connectionError
          } else {
            this.error = 'Failed to establish SignalR connection. Please try again.'
          }
          step.running = false
          this.provisioning = false
          return
        }

        try {
          const requestBody = {
            tenantName: this.tenantName,
            adminUserId: String(userId),
            stepName: step.stage,
            tenantId: this.newTenantId || null,
            templateDb: this.templateDb || null
          }

          // Add step-specific parameters
          if (step.key === 'sql' || step.key === 'retention') {
            // SQL Database and Retention Policy steps need template DB
            if (!this.templateDb) {
              this.error = `Template database name is required for ${step.label} step.`
              step.running = false
              return
            }
          }

          const response = await axios.post(`${this.baseApiUrl}/tenantAdmin/runStep`, requestBody)

          // The HTTP response confirms the step was started (202 Accepted)
          // Don't mark as done here - wait for SignalR messages to update status
          // The step is already set to running = true, and SignalR will update it when complete
          if (response.status === 202 || response.data.success) {
            // Store tenant ID if returned
            if (response.data.tenantId) {
              this.newTenantId = response.data.tenantId
            }
            // Keep step.running = true - SignalR will update it when complete
            // Store result when SignalR confirms completion
          } else {
            // If the API returns an error immediately, mark as failed
            step.running = false
            throw new Error(response.data.error || response.data.message || 'Step failed to start')
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('RunStep error', err)
          this.error = err?.response?.data?.error || err?.message || `Failed to run step: ${step.label}`
          step.running = false
          this.provisioning = false
        }
      },
      async loadTenants() {
        this.loadingTenants = true
        try {
          const response = await axios.get(`${this.baseApiUrl}/tenantAdmin/tenants`)
          this.tenants = response.data || []
          if (!this.tenants.some(t => t.dbName === 'atom_trial')) {
            this.tenants.push({ dbName: 'atom_trial', tenantName: 'atom_trial' })
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading tenants:', error)
          this.error = error?.response?.data?.message || 'Failed to load tenants'
        } finally {
          this.loadingTenants = false
        }
      }
    },
    created() {
      this.loadTenants()
    }
  }
</script>
